import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
} from "./types"

/**
 * Canvas or inspector node a Leo apply writes into.
 * Wired as `[data-leo-apply="<anchor>"]` on the field that should receive motion.
 */
export type LeoApplyAnchor =
  | "title"
  | "leadIn"
  | "rationale"
  | "references"
  | "bloom"
  | "difficulty"
  | "category"
  | "format"
  | "shuffle"
  | "options"
  | `option:${string}`

export const LEO_APPLY_ATTR = "data-leo-apply"
export const LEO_APPLY_RECEIVING_ATTR = "data-leo-receiving"
export const LEO_APPLY_FOCUS_ATTR = "data-leo-focus"
export const LEO_APPLY_INK_ATTR = "data-leo-apply-ink"
export const LEO_APPLY_STAGGER_MS = 80

export type LeoApplyGhosts = Partial<Record<LeoApplyAnchor, string>>

/** Stem, option, and rationale writes use glyph ink. Classification uses a quieter sheen. */
export function leoApplyIsTextAnchor(anchor: LeoApplyAnchor): boolean {
  return (
    anchor === "title" ||
    anchor === "leadIn" ||
    anchor === "rationale" ||
    anchor === "references" ||
    anchor.startsWith("option:")
  )
}

/** Previous copy to wipe away. Empty or unchanged fields skip the ghost. */
export function leoApplyGhostText(previous: string, next: string): string | null {
  const from = previous.replace(/\s+/g, " ").trim()
  const to = next.replace(/\s+/g, " ").trim()
  if (!from || from === to) return null
  return previous.trim()
}

/**
 * Map an accepted suggestion to the field(s) that change.
 *
 * | CTA / kind | Lands on |
 * | --- | --- |
 * | Use this stem | Question |
 * | Use this title | Title |
 * | Use this distractor | That distractor row |
 * | Use these options / Replace both | Each rewritten option row |
 * | Add options | New rows when ids are known, else Answer choices |
 * | Use this explanation | Explanation |
 * | Mark the negative / references | References |
 * | Bloom / difficulty / format | Matching Properties control |
 * | Turn shuffle off | Answer choices |
 * | Pin option | That option row |
 * | Ask Leo / none | No canvas write |
 */
export function leoApplyAnchors(
  check: QuestionQualityCheck,
  alternate?: QuestionQualityAlternate | null,
  extras?: { addedOptionIds?: string[] },
): LeoApplyAnchor[] {
  const kind = alternate?.actionKind ?? check.actionKind
  const patch = alternate?.patch ?? check.patch
  const anchors: LeoApplyAnchor[] = []

  const add = (anchor: LeoApplyAnchor) => {
    if (!anchors.includes(anchor)) anchors.push(anchor)
  }

  if (kind === "open-leo" || kind === "none") return []

  if (kind === "set-bloom") add("bloom")
  else if (kind === "set-difficulty") add("difficulty")
  else if (kind === "set-classification") {
    if (patch?.bloom) add("bloom")
    if (patch?.difficulty) add("difficulty")
    if (patch?.categoryIds?.length) add("category")
  }
  else if (kind === "change-format") {
    add("format")
    if (patch?.leadIn) add("leadIn")
  } else if (kind === "turn-shuffle-off") add("options")
  else if (kind === "pin-option") {
    if (check.optionId) add(`option:${check.optionId}`)
    else add("options")
  } else if (kind === "set-rationale") add("rationale")
  else if (kind === "apply-text") {
    if (patch?.leadIn) add("leadIn")
    if (patch?.title) add("title")
    if (patch?.optionText && check.optionId) add(`option:${check.optionId}`)
    if (patch?.optionTexts?.length) {
      for (const row of patch.optionTexts) add(`option:${row.id}`)
    }
    if (patch?.addOptions?.length) {
      if (extras?.addedOptionIds?.length) {
        for (const id of extras.addedOptionIds) add(`option:${id}`)
      } else {
        add("options")
      }
    }
    if (patch?.referencesHtml || patch?.referenceFileName) add("references")
  }

  if (anchors.length === 0) addFieldFallback(check, add)
  return anchors
}

export function leoApplyAnnouncement(anchors: LeoApplyAnchor[]): string {
  const labels = uniqueLabels(anchors)
  if (labels.length === 0) return "Leo updated this question."
  if (labels.length === 1) return `Leo updated ${labels[0]}.`
  if (labels.length === 2) return `Leo updated ${labels[0]} and ${labels[1]}.`
  return `Leo updated ${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}.`
}

export const CLASSIFICATION_APPLY_ANCHORS: readonly LeoApplyAnchor[] = [
  "bloom",
  "difficulty",
  "category",
]

export const CLASSIFICATION_SCROLL_ID = "composer-classification"

export function leoApplyScrollsClassification(
  anchors: readonly LeoApplyAnchor[],
): boolean {
  return anchors.some((anchor) =>
    CLASSIFICATION_APPLY_ANCHORS.includes(anchor),
  )
}

export function leoApplyScrollTarget(
  anchors: readonly LeoApplyAnchor[],
): { id: string } | { selector: string } | null {
  if (leoApplyScrollsClassification(anchors)) {
    return { id: CLASSIFICATION_SCROLL_ID }
  }
  const first = anchors[0]
  if (!first) return null
  return { selector: leoApplySelector(first) }
}

export function leoApplySelector(anchor: LeoApplyAnchor): string {
  return `[${LEO_APPLY_ATTR}="${cssEscape(anchor)}"]`
}

/** Draft / variation writes that are not a quality check. */
export function leoApplyAnchorsFromDraftPatch(patch: {
  title?: unknown
  leadIn?: unknown
  options?: unknown
  rationale?: unknown
  references?: unknown
  bloom?: unknown
  difficulty?: unknown
  categoryIds?: unknown
  type?: unknown
}): LeoApplyAnchor[] {
  const anchors: LeoApplyAnchor[] = []
  if (patch.title !== undefined) anchors.push("title")
  if (patch.leadIn !== undefined) anchors.push("leadIn")
  if (patch.options !== undefined) anchors.push("options")
  if (patch.rationale !== undefined) anchors.push("rationale")
  if (patch.references !== undefined) anchors.push("references")
  if (patch.bloom !== undefined) anchors.push("bloom")
  if (patch.difficulty !== undefined) anchors.push("difficulty")
  if (patch.categoryIds !== undefined) anchors.push("category")
  if (patch.type !== undefined) anchors.push("format")
  return anchors
}

/** A field wrapper washes only when that exact field is in the apply set. */
export function leoApplyFieldIsReceiving(
  anchor: LeoApplyAnchor | undefined,
  receiving: readonly LeoApplyAnchor[],
): boolean {
  return Boolean(anchor && receiving.includes(anchor))
}

function addFieldFallback(
  check: QuestionQualityCheck,
  add: (anchor: LeoApplyAnchor) => void,
) {
  if (check.field === "option" && check.optionId) {
    add(`option:${check.optionId}`)
    return
  }
  if (check.field === "options") {
    add("options")
    return
  }
  if (check.field === "settings") {
    if (check.settingsTarget === "bloom") add("bloom")
    else if (check.settingsTarget === "difficulty") add("difficulty")
    else if (check.settingsTarget === "classification") {
      add("bloom")
      add("difficulty")
      add("category")
    }
    else if (check.settingsTarget === "format") add("format")
    else if (check.settingsTarget === "shuffle") add("options")
    return
  }
  if (
    check.field === "title" ||
    check.field === "leadIn" ||
    check.field === "rationale" ||
    check.field === "references"
  ) {
    add(check.field)
  }
}

function uniqueLabels(anchors: LeoApplyAnchor[]): string[] {
  const labels: string[] = []
  const seen = new Set<string>()
  for (const anchor of anchors) {
    const label = labelFor(anchor)
    if (seen.has(label)) continue
    seen.add(label)
    labels.push(label)
  }
  return labels
}

function labelFor(anchor: LeoApplyAnchor): string {
  if (anchor === "title") return "the title"
  if (anchor === "leadIn") return "the question"
  if (anchor === "rationale") return "the explanation"
  if (anchor === "references") return "references"
  if (anchor === "bloom") return "Bloom's taxonomy"
  if (anchor === "difficulty") return "difficulty"
  if (anchor === "category") return "category"
  if (anchor === "format") return "the question format"
  return "answer choices"
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value)
  }
  return value.replace(/["\\]/g, "\\$&")
}
